import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { HR_TEAM_LOCATIONS } from "../../config/locationConfig";
import AdminIcon from "./adminIcons";
import { ADMIN_BUTTON_SX, ADMIN_COLORS, ADMIN_ICON_SIZE, ADMIN_TYPE } from "./adminUi";
import {
  ADMIN_FORM_GRID_SX,
  ADMIN_INPUT_SX,
  AdminDialog,
  AdminField,
  AdminTextField,
  PhonePrefix,
  submitWithValidation,
} from "./adminForm";
import { EMAIL_REGEX, FIELD_MAX, fieldSchema, sanitize } from "./adminValidation";

export var COMPANY_OPTIONS = [
  "Spanidea Digital Pvt. Ltd.",
  "Spanidea Systems Pvt. Ltd.",
];

export var EMPTY_JOINEE_VALUES = {
  companyName: "",
  email: "",
  name: "",
  designation: "",
  workLocation: "",
  doj: null,
  aadhaar: "",
  pan: "",
  phoneNumber: "",
  recipients: [],
};

/** 4th character is the holder type (P = individual, C = company, …). */
var PAN_REGEX = /^[A-Z]{3}[ABCFGHJLPT][A-Z][0-9]{4}[A-Z]$/;
var AADHAAR_REGEX = /^[2-9][0-9]{11}$/;

export function formatAadhaar(digits) {
  return String(digits || "")
    .replace(/\D/g, "")
    .slice(0, 12)
    .replace(/(\d{4})(?=\d)/g, "$1 ");
}

/** Keeps only characters valid for their PAN position: AAAAA9999A. */
function sanitizePan(raw) {
  var chars = String(raw || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .split("");
  var out = "";
  for (var i = 0; i < chars.length && out.length < 10; i++) {
    var pos = out.length;
    var wantsDigit = pos >= 5 && pos <= 8;
    if (wantsDigit ? /[0-9]/.test(chars[i]) : /[A-Z]/.test(chars[i])) {
      out += chars[i];
    }
  }
  return out;
}

function buildSchema(originalDoj) {
  return Yup.object({
    companyName: Yup.string().required("Please select a company"),
    email: fieldSchema.email("Candidate email"),
    name: fieldSchema.name(),
    designation: fieldSchema.designation(),
    workLocation: fieldSchema.select("Please select a work location", HR_TEAM_LOCATIONS),
    doj: Yup.mixed()
      .test("required", "Date of joining is required", function (value) {
        return Boolean(value);
      })
      .test("valid", "Enter a valid date (DD/MM/YYYY)", function (value) {
        return !value || dayjs(value).isValid();
      })
      .test(
        "not-past",
        "Date of joining must be today or a future date",
        function (value) {
          if (!value || !dayjs(value).isValid()) return true;
          var picked = dayjs(value);
          if (originalDoj && picked.isSame(dayjs(originalDoj), "day")) return true;
          return !picked.isBefore(dayjs(), "day");
        }
      ),
    aadhaar: Yup.string()
      .required("Aadhaar number is required")
      .length(12, "Aadhaar number must be 12 digits")
      .matches(AADHAAR_REGEX, "Aadhaar number cannot start with 0 or 1"),
    pan: Yup.string()
      .required("PAN is required")
      .length(10, "PAN must be 10 characters (e.g. ABCPE1234F)")
      .matches(PAN_REGEX, "Enter a valid PAN (e.g. ABCPE1234F)"),
    phoneNumber: fieldSchema.phone(),
    recipients: Yup.array().of(Yup.string().matches(EMAIL_REGEX)),
  });
}

function CalendarIcon() {
  return <AdminIcon name="CalendarDots" size={ADMIN_ICON_SIZE.button} sx={{ color: ADMIN_COLORS.label }} />;
}

/**
 * Add / edit candidate form.
 * `originalDoj` (edit only) stays selectable even if it is now in the past.
 */
function NewJoineeDialog(props) {
  var isEdit = props.mode === "edit";
  var originalDoj = isEdit ? props.originalDoj || "" : "";
  var [ccError, setCcError] = useState("");

  var formik = useFormik({
    initialValues: props.initialValues || EMPTY_JOINEE_VALUES,
    enableReinitialize: true,
    validationSchema: buildSchema(originalDoj),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: function (values, helpers) {
      return Promise.resolve(props.onSubmit(values)).finally(function () {
        helpers.setSubmitting(false);
      });
    },
  });

  var values = formik.values;

  function fieldError(name) {
    return formik.touched[name] && formik.errors[name] ? formik.errors[name] : "";
  }

  function setValue(name, value) {
    formik.setFieldTouched(name, true, false);
    formik.setFieldValue(name, value, true);
  }

  function handleClose() {
    if (formik.isSubmitting) return;
    setCcError("");
    formik.resetForm();
    props.onClose();
  }

  function handleRecipients(nextValues) {
    var cleaned = [];
    var invalid = "";
    nextValues.forEach(function (raw) {
      var email = String(raw || "").trim();
      if (!email) return;
      if (!EMAIL_REGEX.test(email) || email.length > FIELD_MAX.email) {
        invalid = email;
        return;
      }
      if (cleaned.indexOf(email) === -1) cleaned.push(email);
    });
    setCcError(invalid ? '"' + invalid + '" is not a valid email' : "");
    formik.setFieldValue("recipients", cleaned, false);
  }

  function isDateDisabled(day) {
    if (!day.isBefore(dayjs(), "day")) return false;
    return !(originalDoj && day.isSame(dayjs(originalDoj), "day"));
  }

  var dojError = fieldError("doj");
  var hasLegacyLocation =
    values.workLocation && HR_TEAM_LOCATIONS.indexOf(values.workLocation) === -1;

  return (
    <AdminDialog
      open={props.open}
      onClose={handleClose}
      busy={formik.isSubmitting}
      title={isEdit ? "Edit Candidate" : "Add Candidate"}
      titleId="candidate-dialog-title"
      onSubmit={submitWithValidation(formik)}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} disabled={formik.isSubmitting} sx={ADMIN_BUTTON_SX.outlined}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            startIcon={formik.isSubmitting ? <CircularProgress size={ADMIN_ICON_SIZE.button} color="inherit" /> : null}
            sx={ADMIN_BUTTON_SX.contained}
          >
            {isEdit ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <Box sx={ADMIN_FORM_GRID_SX}>
        <AdminTextField
          select
          required
          label="Company Branch"
          name="companyName"
          placeholder="Select Branch"
          value={values.companyName || ""}
          onChange={function (e) {
            setValue("companyName", e.target.value);
          }}
          onBlur={formik.handleBlur}
          error={fieldError("companyName")}
        >
          {COMPANY_OPTIONS.map(function (company) {
            return (
              <MenuItem key={company} value={company}>
                {company}
              </MenuItem>
            );
          })}
        </AdminTextField>

        <AdminTextField
          required
          type="email"
          label="Candidate Email"
          name="email"
          placeholder="name@example.com"
          value={values.email}
          onChange={function (e) {
            setValue("email", sanitize.email(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: FIELD_MAX.email }}
          error={fieldError("email")}
          hint="Login details will be sent here"
        />

        <AdminTextField
          required
          label="Full Name"
          name="name"
          placeholder="Write Name"
          value={values.name}
          onChange={function (e) {
            setValue("name", sanitize.name(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: FIELD_MAX.name }}
          error={fieldError("name")}
        />

        <AdminTextField
          required
          label="Designation"
          name="designation"
          placeholder="Write Designation"
          value={values.designation}
          onChange={function (e) {
            setValue("designation", sanitize.designation(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: FIELD_MAX.designation }}
          error={fieldError("designation")}
        />

        <AdminTextField
          select
          required
          label="Location"
          name="workLocation"
          placeholder="Select Location"
          value={values.workLocation || ""}
          onChange={function (e) {
            setValue("workLocation", e.target.value);
          }}
          onBlur={formik.handleBlur}
          disabled={!props.canChooseWorkLocation}
          error={fieldError("workLocation")}
          hint={!props.canChooseWorkLocation ? "Fixed to your HR team location" : ""}
        >
          {hasLegacyLocation ? (
            <MenuItem value={values.workLocation} disabled>
              {values.workLocation} (invalid location)
            </MenuItem>
          ) : null}
          {HR_TEAM_LOCATIONS.map(function (location) {
            return (
              <MenuItem key={location} value={location}>
                {location}
              </MenuItem>
            );
          })}
        </AdminTextField>

        <AdminField label="Date Of Joining" required error={dojError} htmlFor="admin-field-doj">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              value={values.doj ? dayjs(values.doj) : null}
              shouldDisableDate={isDateDisabled}
              onChange={function (date) {
                setValue("doj", date || null);
              }}
              slots={{ openPickerIcon: CalendarIcon }}
              slotProps={{
                textField: {
                  id: "admin-field-doj",
                  fullWidth: true,
                  name: "doj",
                  onBlur: function () {
                    formik.setFieldTouched("doj", true, true);
                  },
                  error: Boolean(dojError),
                  sx: ADMIN_INPUT_SX,
                },
              }}
            />
          </LocalizationProvider>
        </AdminField>

        <AdminTextField
          required
          label="Aadhaar Number"
          name="aadhaar"
          placeholder="Write Aadhaar Number"
          value={formatAadhaar(values.aadhaar)}
          onChange={function (e) {
            setValue("aadhaar", e.target.value.replace(/\D/g, "").slice(0, 12));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ inputMode: "numeric", maxLength: 14 }}
          error={fieldError("aadhaar")}
        />

        <AdminTextField
          required
          label="PAN Number"
          name="pan"
          placeholder="Write PAN Number"
          value={values.pan}
          onChange={function (e) {
            setValue("pan", sanitizePan(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }}
          error={fieldError("pan")}
        />

        <AdminTextField
          required
          label="Phone Number"
          name="phoneNumber"
          placeholder="Write Phone Number"
          value={values.phoneNumber}
          onChange={function (e) {
            setValue("phoneNumber", sanitize.phone(e.target.value));
          }}
          onBlur={formik.handleBlur}
          inputProps={{ inputMode: "numeric", maxLength: FIELD_MAX.phone }}
          InputProps={{ startAdornment: <PhonePrefix /> }}
          error={fieldError("phoneNumber")}
        />

        <AdminField label="CC" error={ccError} hint="Optional. These addresses are copied on the welcome email." htmlFor="admin-field-cc">
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            id="admin-field-cc"
            options={[]}
            value={values.recipients || []}
            onChange={function (event, newValue) {
              handleRecipients(newValue);
            }}
            renderTags={function (tagValue, getTagProps) {
              return tagValue.map(function (option, index) {
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{
                      height: 28,
                      fontSize: ADMIN_TYPE.helper.fontSize,
                      bgcolor: ADMIN_COLORS.brandSoft,
                      color: ADMIN_COLORS.brand,
                      "& .MuiChip-deleteIcon": { color: ADMIN_COLORS.brand },
                    }}
                  />
                );
              });
            }}
            renderInput={function (params) {
              return (
                <TextField
                  {...params}
                  placeholder={values.recipients && values.recipients.length ? "" : "Write CC"}
                  error={Boolean(ccError)}
                  sx={[
                    ADMIN_INPUT_SX,
                    { "& .MuiOutlinedInput-root": { py: "3px !important", pl: "6px !important" } },
                    { "& .MuiOutlinedInput-root .MuiAutocomplete-input": { py: "6px" } },
                  ]}
                />
              );
            }}
          />
        </AdminField>
      </Box>
    </AdminDialog>
  );
}

export default NewJoineeDialog;
